// Notifications: what a turn (or a live feed refresh) raises, and where it goes.
//
//   world changes ──┐                      ┌─► cards  top MAX_CARDS with priority ≥ CARD_MIN, on the stack
//   event bullets ──┼─► Notification[] ────┤
//   live records ───┘   (priority 0..100)  └─► log    everything with priority ≥ LOG_MIN, newest first
//
// Event bullets are scored here (a week has ~85 of them; a card each would bury the map):
//
//   category base  +30 names the leader's state  +15 names a state at war with / allied to / denouncing it
//   +10 has a map location  +5 cited three or more times          capped at MAX_PRIORITY
//
// Ids are stable, so raising the same turn twice never posts a card twice.

import type { DateStr, SourceId, WorldEvent } from '../core/schema.ts';
import type { Relation, WorldSnapshot } from '../world/api.ts';
import type { WorldChange } from '../world/changes.ts';
import type { Notification, NotificationKind } from './api.ts';
import type { Store } from './state.ts';

export const MAX_PRIORITY = 100;
/** Priority a notification needs to appear as a card. */
export const CARD_MIN = 40;
/** Priority a notification needs to be logged at all. */
export const LOG_MIN = 30;
/** Cards raised by one turn or refresh, the highest priority first. */
export const MAX_CARDS = 5;
/** Cards standing on the stack; the lowest priority is dropped first. */
export const MAX_STACK = 8;
/** Entries kept in the log. */
export const MAX_LOG = 1000;
/** Cards from the live feeds carry this id prefix: they describe the present, not a leader's turn. */
export const LIVE_ID_PREFIX = 'live:';
/** Event bullets logged per turn (the log is for scanning, not for a week's whole wire). */
export const MAX_LOGGED_EVENTS = 25;

const TITLE_MAX = 110;
const OWN_STATE_BONUS = 30;
const RELATED_STATE_BONUS = 15;
const LOCATED_BONUS = 10;
const WELL_CITED_BONUS = 5;
const WELL_CITED_REFS = 3;
const DEFAULT_CATEGORY_BASE = 10;
const EVENT_SOURCE_PREFIX = 'evt:wp-ce-';

/** Portal headings (schema WorldEvent.cat) by how likely a bullet is to matter to a leader. */
const CATEGORY_BASE: Record<string, number> = {
  'Armed conflicts and attacks': 30,
  'International relations': 30,
  'Politics and elections': 25,
  'Disasters and accidents': 25,
  'Science and technology': 20,
  'Business and economy': 15,
  'Health and environment': 15,
  'Law and crime': 10,
  'Arts and culture': 5,
  'Sports': 5,
  'Music and media': 5,
};

const RELATED: readonly Relation[] = ['war', 'ally', 'denounced'];

/** Who the scoring is for. */
export interface Viewer {
  iso3: string;
  snapshot: WorldSnapshot;
  /** Country iso3 → actor index. */
  indexOf(iso3: string): number;
  /** True when the id is a registered source (world.source(id) resolves). */
  hasSource(id: SourceId): boolean;
}

/** Words whose period does not end a sentence: "Dr. Roe", "Apple Inc. Shares". */
const ABBREVIATIONS = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'sr', 'jr', 'st', 'mt', 'ft', 'gen', 'col', 'lt', 'sgt', 'maj', 'capt', 'adm',
  'sen', 'rep', 'gov', 'rev', 'inc', 'corp', 'co', 'ltd', 'vs', 'v',
]);

/** A period, "?" or "!" then whitespace and a capital: where a sentence may end. */
const SENTENCE_END = /[.!?]\s+(?=[A-Z])/g;

/** "J." (an initial), "U.S." and "p.m." (dotted), or a listed abbreviation: not a sentence end. */
function endsSentence(before: string): boolean {
  const word = /[\p{L}.]*$/u.exec(before)?.[0] ?? '';
  if (word.length === 1 && word === word.toUpperCase()) {
    return false;
  }
  return !word.includes('.') && !ABBREVIATIONS.has(word.toLowerCase());
}

/** First sentence, cut at TITLE_MAX; without a sentence end it is the text, cut the same way. */
function headline(text: string): string {
  const end = [...text.matchAll(SENTENCE_END)].find((m) => endsSentence(text.slice(0, m.index)));
  const first = end ? text.slice(0, end.index + 1) : text;
  return first.length > TITLE_MAX ? `${first.slice(0, TITLE_MAX - 1).trimEnd()}…` : first;
}

/** Small stable hash, so an event bullet keeps its id across sessions. */
function hash(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = (Math.imul(h, 31) + text.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

function relatedTo(v: Viewer, iso3s: string[]): boolean {
  return iso3s.some((id) => {
    const info = v.snapshot.relations.get(v.indexOf(id));
    return info !== undefined && RELATED.includes(info.relation);
  });
}

export function scoreEvent(v: Viewer, e: WorldEvent): number {
  let score = CATEGORY_BASE[e.cat] ?? DEFAULT_CATEGORY_BASE;

  if (e.c.includes(v.iso3)) {
    score += OWN_STATE_BONUS;
  } else if (relatedTo(v, e.c)) {
    score += RELATED_STATE_BONUS;
  }
  if (e.hex !== undefined) {
    score += LOCATED_BONUS;
  }
  if (e.refs.length >= WELL_CITED_REFS) {
    score += WELL_CITED_BONUS;
  }
  return Math.min(MAX_PRIORITY, score);
}

function fromEvent(v: Viewer, e: WorldEvent): Notification {
  const source: SourceId = `${EVENT_SOURCE_PREFIX}${e.d}`;
  return {
    id: `evt:${e.d}:${hash(e.t)}`,
    kind: 'event',
    date: e.d,
    title: headline(e.t),
    body: e.t,
    hex: e.hex,
    iso3s: e.c,
    sources: v.hasSource(source) ? [source] : [],
    links: e.refs,
    priority: scoreEvent(v, e),
  };
}

/** The contract has no diplomacy kind: a relation flip into or out of war reads as 'war', the rest as 'event'. */
function kindOfChange(c: WorldChange): NotificationKind {
  if (c.kind !== 'relation') {
    return c.kind;
  }
  return c.relation?.from === 'war' || c.relation?.to === 'war' ? 'war' : 'event';
}

function fromChange(c: WorldChange): Notification {
  return {
    id: c.id, kind: kindOfChange(c), date: c.date, title: c.title, body: c.body, hex: c.hex, iso3s: c.iso3s,
    sources: c.sources, links: [], priority: c.priority,
  };
}

function byPriority(a: Notification, b: Notification): number {
  if (b.priority !== a.priority) {
    return b.priority - a.priority;
  }
  return a.date < b.date ? 1 : a.date > b.date ? -1 : 0;
}

export interface TurnNotes {
  cards: Notification[];
  log: Notification[];
}

/** Score one turn's changes and event bullets and split them into cards and log entries. */
export function turnNotes(v: Viewer, changes: WorldChange[], events: WorldEvent[]): TurnNotes {
  const scored = events.map((e) => fromEvent(v, e)).filter((n) => n.priority >= LOG_MIN).sort(byPriority);
  const fromWorld = changes.map(fromChange);
  const all = [...fromWorld, ...scored.slice(0, MAX_LOGGED_EVENTS)].sort(byPriority);

  return {
    cards: all.filter((n) => n.priority >= CARD_MIN).slice(0, MAX_CARDS),
    log: all.filter((n) => n.priority >= LOG_MIN),
  };
}

/** A notice that the service could not do its job, so the gap is visible instead of silent. */
export function notice(id: string, date: DateStr, title: string, body: string, kind: NotificationKind = 'event'): Notification {
  return { id, kind, date, title, body, iso3s: [], sources: [], links: [], priority: CARD_MIN };
}

// ─── into the store ──────────────────────────────────────────────────────────────────────

/** Post cards on the stack (lowest priority dropped past MAX_STACK) and entries in the log (newest first). */
export function post(store: Store, notes: TurnNotes): void {
  const standing = new Set(store.notifications.peek().map((n) => n.id));
  const fresh = notes.cards.filter((n) => !standing.has(n.id));
  if (fresh.length > 0) {
    store.notifications.value = [...store.notifications.peek(), ...fresh].sort(byPriority).slice(0, MAX_STACK);
  }

  const logged = new Set(store.log.peek().map((n) => n.id));
  const added = notes.log.filter((n) => !logged.has(n.id));
  if (added.length > 0) {
    store.log.value = [...added.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)), ...store.log.peek()].slice(0, MAX_LOG);
  }
}

